import { useCallback, useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  getRabattRegeln,
  saveRabattRegeln,
  type RabattRegel,
} from "@/lib/artikelRabatt";
import {
  getZuschlagRegeln,
  saveZuschlagRegeln,
  type ZuschlagRegel,
} from "@/lib/artikelZuschlag";
import { toast } from "@/lib/toast";

type ZuschlagDraft = {
  key: string;
  ab_ek_netto: string;
  zuschlag_prozent: string;
};

type RabattDraft = {
  key: string;
  ab_listenpreis_netto: string;
  rabatt_prozent: string;
};

function toZuschlagDraft(regeln: ZuschlagRegel[]): ZuschlagDraft[] {
  return regeln.map((r, index) => ({
    key: r.id != null ? `z-id-${r.id}` : `z-new-${index}`,
    ab_ek_netto: String(r.ab_ek_netto ?? ""),
    zuschlag_prozent: String(r.zuschlag_prozent ?? ""),
  }));
}

function toRabattDraft(regeln: RabattRegel[]): RabattDraft[] {
  return regeln.map((r, index) => ({
    key: r.id != null ? `r-id-${r.id}` : `r-new-${index}`,
    ab_listenpreis_netto: String(r.ab_listenpreis_netto ?? ""),
    rabatt_prozent: String(r.rabatt_prozent ?? ""),
  }));
}

function parseNum(raw: string): number | null {
  const s = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function ArtikelEinstellungenPage() {
  const [zuschlagRows, setZuschlagRows] = useState<ZuschlagDraft[]>([]);
  const [rabattRows, setRabattRows] = useState<RabattDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [zuschlag, rabatt] = await Promise.all([getZuschlagRegeln(), getRabattRegeln()]);
      setZuschlagRows(toZuschlagDraft(zuschlag));
      setRabattRows(toRabattDraft(rabatt));
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Einstellungen konnten nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  function parseZuschlagRows(): ZuschlagRegel[] | null {
    const parsed: ZuschlagRegel[] = [];
    const seen = new Set<number>();
    for (const row of zuschlagRows) {
      const ab = parseNum(row.ab_ek_netto);
      const pct = parseNum(row.zuschlag_prozent);
      if (ab == null && pct == null && !row.ab_ek_netto.trim() && !row.zuschlag_prozent.trim()) {
        continue;
      }
      if (ab == null || pct == null || ab < 0 || pct < 0) {
        setError("Bitte gültige Werte für „ab EK“ und „Zuschlag %“ eingeben (≥ 0).");
        return null;
      }
      const abRounded = Math.round(ab * 100) / 100;
      if (seen.has(abRounded)) {
        setError(`Doppelte Untergrenze „ab EK“: ${abRounded.toFixed(2)} €.`);
        return null;
      }
      seen.add(abRounded);
      parsed.push({ ab_ek_netto: ab, zuschlag_prozent: pct });
    }
    parsed.sort((a, b) => a.ab_ek_netto - b.ab_ek_netto);
    return parsed;
  }

  function parseRabattRows(): RabattRegel[] | null {
    const parsed: RabattRegel[] = [];
    const seen = new Set<number>();
    for (const row of rabattRows) {
      const ab = parseNum(row.ab_listenpreis_netto);
      const pct = parseNum(row.rabatt_prozent);
      if (
        ab == null &&
        pct == null &&
        !row.ab_listenpreis_netto.trim() &&
        !row.rabatt_prozent.trim()
      ) {
        continue;
      }
      if (ab == null || pct == null || ab < 0 || pct < 0 || pct > 100) {
        setError(
          "Bitte gültige Werte für „ab Listenpreis“ (≥ 0) und „Rabatt %“ (0–100) eingeben.",
        );
        return null;
      }
      const abRounded = Math.round(ab * 100) / 100;
      if (seen.has(abRounded)) {
        setError(`Doppelte Untergrenze „ab Listenpreis“: ${abRounded.toFixed(2)} €.`);
        return null;
      }
      seen.add(abRounded);
      parsed.push({ ab_listenpreis_netto: ab, rabatt_prozent: pct });
    }
    parsed.sort((a, b) => a.ab_listenpreis_netto - b.ab_listenpreis_netto);
    return parsed;
  }

  async function handleSave() {
    const zuschlag = parseZuschlagRows();
    if (zuschlag == null) return;
    const rabatt = parseRabattRows();
    if (rabatt == null) return;

    setSaving(true);
    setError(null);
    try {
      const [savedZuschlag, savedRabatt] = await Promise.all([
        saveZuschlagRegeln(zuschlag),
        saveRabattRegeln(rabatt),
      ]);
      setZuschlagRows(toZuschlagDraft(savedZuschlag));
      setRabattRows(toRabattDraft(savedRabatt));
      toast.success("Zuschlags- und Rabattstaffeln gespeichert.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell
      title="Artikel · Einstellungen"
      subtitle="Zuschlagsstaffeln (EK → VK) und Rabattstaffeln (Listenpreis → EK)."
      back={{ to: "/bestellwesen/artikel", label: "zurück zu Artikel" }}
      actions={
        <Button type="button" onClick={() => void handleSave()} disabled={loading || saving}>
          {saving ? <Spinner className="h-4 w-4" /> : "Speichern"}
        </Button>
      }
    >
      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-label">Zuschlagsstaffeln</h2>
            <p className="mt-1 max-w-2xl text-sm text-label-secondary">
              Pro Staffel: Untergrenze „ab EK“ und Zuschlag in Prozent. Für einen Artikel gilt die
              höchste Regel mit EK ≥ ab EK. VK = EK × (1 + Zuschlag/100).
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              setZuschlagRows((prev) => [
                ...prev,
                { key: `z-new-${Date.now()}`, ab_ek_netto: "", zuschlag_prozent: "" },
              ])
            }
            disabled={loading || saving}
          >
            Zeile hinzufügen
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-ios border border-separator">
            <table className="w-full min-w-[28rem] text-sm">
              <thead className="bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">ab EK (€)</th>
                  <th className="px-3 py-2 text-left font-semibold">Zuschlag %</th>
                  <th className="px-3 py-2 text-right font-semibold"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-separator bg-card">
                {zuschlagRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-label-tertiary">
                      Noch keine Staffeln. Ohne Staffeln bleibt der Verkaufspreis manuell wie bisher.
                    </td>
                  </tr>
                ) : (
                  zuschlagRows.map((row) => (
                    <tr key={row.key}>
                      <td className="px-3 py-2">
                        <TextInput
                          value={row.ab_ek_netto}
                          onChange={(e) =>
                            setZuschlagRows((prev) =>
                              prev.map((r) =>
                                r.key === row.key ? { ...r, ab_ek_netto: e.target.value } : r,
                              ),
                            )
                          }
                          inputMode="decimal"
                          aria-label="ab EK"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <TextInput
                          value={row.zuschlag_prozent}
                          onChange={(e) =>
                            setZuschlagRows((prev) =>
                              prev.map((r) =>
                                r.key === row.key
                                  ? { ...r, zuschlag_prozent: e.target.value }
                                  : r,
                              ),
                            )
                          }
                          inputMode="decimal"
                          aria-label="Zuschlag %"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setZuschlagRows((prev) => prev.filter((r) => r.key !== row.key))
                          }
                          disabled={saving}
                        >
                          Löschen
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-4 rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-label">Rabattstaffeln</h2>
            <p className="mt-1 max-w-2xl text-sm text-label-secondary">
              Pro Staffel: Untergrenze „ab Listenpreis“ und Rabatt in Prozent. Für einen Artikel gilt
              die höchste Regel mit Listenpreis ≥ ab Listenpreis. EK = Listenpreis × (1 −
              Rabatt/100) — also Listenpreis −x&nbsp;%.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              setRabattRows((prev) => [
                ...prev,
                {
                  key: `r-new-${Date.now()}`,
                  ab_listenpreis_netto: "",
                  rabatt_prozent: "",
                },
              ])
            }
            disabled={loading || saving}
          >
            Zeile hinzufügen
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-ios border border-separator">
            <table className="w-full min-w-[28rem] text-sm">
              <thead className="bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">ab Listenpreis (€)</th>
                  <th className="px-3 py-2 text-left font-semibold">Rabatt % (Listenpreis −x %)</th>
                  <th className="px-3 py-2 text-right font-semibold"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-separator bg-card">
                {rabattRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-label-tertiary">
                      Noch keine Rabattstaffeln. Optional zur Ableitung des EK aus dem Listenpreis.
                    </td>
                  </tr>
                ) : (
                  rabattRows.map((row) => (
                    <tr key={row.key}>
                      <td className="px-3 py-2">
                        <TextInput
                          value={row.ab_listenpreis_netto}
                          onChange={(e) =>
                            setRabattRows((prev) =>
                              prev.map((r) =>
                                r.key === row.key
                                  ? { ...r, ab_listenpreis_netto: e.target.value }
                                  : r,
                              ),
                            )
                          }
                          inputMode="decimal"
                          aria-label="ab Listenpreis"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <TextInput
                          value={row.rabatt_prozent}
                          onChange={(e) =>
                            setRabattRows((prev) =>
                              prev.map((r) =>
                                r.key === row.key
                                  ? { ...r, rabatt_prozent: e.target.value }
                                  : r,
                              ),
                            )
                          }
                          inputMode="decimal"
                          aria-label="Rabatt %"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setRabattRows((prev) => prev.filter((r) => r.key !== row.key))
                          }
                          disabled={saving}
                        >
                          Löschen
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AppShell>
  );
}
